import React, { type ReactNode } from "react";
import { ConvexError } from "convex/values";

interface Props {
  label: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Whether a query refused because the record does not exist or is not the
// viewer's (convex/observability answers both with the same 404). That answer
// is a ConvexError with code 404 in its `data`, and nothing else is: a
// ConvexError's message is the client's call trace, and a plain error has no
// code, so neither is read for "not found" (a missing index says it too).
function isNotFound(error: Error): boolean {
  return error instanceof ConvexError && (error.data as { code?: unknown } | null)?.code === 404;
}

// Keeps one section of a page failing on its own. A Convex query that errors
// throws into the nearest boundary; without this one the whole Security Room
// would go blank. Retry remounts the section, which subscribes again.
export class SectionBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    console.error(`SectionBoundary[${this.props.label}]`, error);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (isNotFound(error)) {
      return (
        <div className="sr-ai-error" role="alert">
          {this.props.label} was not found, or you do not have access to it.
        </div>
      );
    }
    return (
      <div className="sr-ai-error" role="alert">
        <span>{this.props.label} could not load.</span>
        <button
          type="button"
          className="sr-btn sr-btn--ghost"
          onClick={() => this.setState({ error: null })}
        >
          Retry
        </button>
      </div>
    );
  }
}
